import { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { LayoutDashboard, Users, FileText, FileSignature, UserPlus, UserCog, LogOut, Shield, Bell, Kanban, ChevronLeft, ChevronRight, Settings, Menu, X, FileBarChart, Wallet } from 'lucide-react';

const AdminLayout = () => {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Estado da sidebar desktop - recupera do localStorage (padrão: recolhida)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('admin-sidebar-collapsed');
    return saved !== null ? saved === 'true' : true;
  });
  
  // Estado do menu mobile
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Salva preferência no localStorage
  useEffect(() => {
    localStorage.setItem('admin-sidebar-collapsed', sidebarCollapsed);
  }, [sidebarCollapsed]);

  // Fecha menu mobile ao mudar de rota
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Previne scroll quando menu mobile está aberto
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
    { icon: Kanban, label: 'Pipeline de Vendas', path: '/admin/sales' },
    { icon: Users, label: 'Clientes', path: '/admin/clients' },
    { icon: FileText, label: 'Documentos', path: '/admin/documents' },
    { icon: FileSignature, label: 'Contratos', path: '/admin/contracts' },
    { icon: Wallet, label: 'Despesas', path: '/admin/expenses' },
    { icon: FileBarChart, label: 'Relatórios', path: '/admin/reports' },
    { icon: Bell, label: 'Enviar Notificação', path: '/admin/notifications/new' },
    { icon: UserPlus, label: 'Novo Cliente', path: '/admin/clients/new' },
  ];

  if (admin?.is_super_admin) {
    menuItems.push({ icon: UserCog, label: 'Administradores', path: '/admin/admins' });
    menuItems.push({ icon: Settings, label: 'Configurações', path: '/admin/settings' });
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-30">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 md:h-16">
            <div className="flex items-center gap-2 md:gap-4">
              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-md"
                aria-label="Menu"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
              
              <Shield className="text-arcano-blue" size={28} />
              <div>
                <h1 className="text-lg md:text-xl font-bold text-white">Gandalf</h1>
                <p className="text-xs text-gray-400 hidden sm:block">Arcano Projetos</p>
              </div>
            </div>
            <div className="flex items-center gap-2 md:gap-4">
              <span className="text-gray-300 hidden lg:block">{admin?.full_name}</span>
              {admin?.is_super_admin && (
                <span className="bg-yellow-500/20 text-yellow-500 px-2 md:px-3 py-1 rounded-full text-xs font-semibold">
                  Super Admin
                </span>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center gap-1 md:gap-2 px-2 md:px-4 py-2 text-red-400 hover:bg-red-900/20 rounded-md transition-colors"
              >
                <LogOut size={20} />
                <span className="hidden md:inline">Sair</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside 
        className={`fixed top-14 left-0 bottom-0 w-64 bg-white z-50 transform transition-transform duration-300 ease-in-out md:hidden shadow-xl ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <nav className="p-4 h-full overflow-y-auto">
          <div className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-arcano-blue text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
          
          {/* Mobile logout */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 w-full text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut size={20} />
              <span>Sair</span>
            </button>
          </div>
        </nav>
      </aside>

      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8">
        <div className="flex gap-4 md:gap-8">
          {/* Desktop Sidebar */}
          <aside 
            className={`flex-shrink-0 transition-all duration-300 ease-in-out hidden md:block ${
              sidebarCollapsed ? 'w-16' : 'w-64'
            }`}
          >
            <nav className={`bg-white rounded-lg shadow-md sticky top-24 ${sidebarCollapsed ? 'p-2' : 'p-4'}`}>
              {/* Toggle Button */}
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="w-full flex items-center justify-center p-2 mb-2 text-gray-500 hover:bg-gray-100 rounded-md transition-colors"
                title={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
              >
                {sidebarCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
              </button>
              
              <div className="space-y-1">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      title={sidebarCollapsed ? item.label : undefined}
                      className={`flex items-center gap-3 px-3 py-3 rounded-md transition-all ${
                        isActive
                          ? 'bg-arcano-blue text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      } ${sidebarCollapsed ? 'justify-center' : ''}`}
                    >
                      <Icon size={20} className="flex-shrink-0" />
                      {!sidebarCollapsed && (
                        <span className="whitespace-nowrap overflow-hidden">{item.label}</span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
